import { Box, Button, HStack, Text } from "@chakra-ui/react";
import Form, { IFormProps, withForm } from "@ui/components/forms";
import PeriodSelector, { IPeriod } from "@ui/components/datetime";
import { DateTime } from "luxon";
import { useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { MealDictionary } from "../MealBooking/MealOption";
import { useSupplierMealsSummary } from "@/services/useSupplierPortal";
import { useSelf } from "@ui/services/useUser";

const Historical = ({ Wrapper, onClose }: IFormProps) => {
  const { data: me } = useSelf<any>();
  const [period, setPeriod] = useState<IPeriod | undefined>({
    name: "This month",
    start: DateTime.now().startOf("month"),
    end: DateTime.now().endOf("month"),
  });

  const {
    data: historicalData,
    isLoading,
    isFetching,
  } = useSupplierMealsSummary(me?.id, period?.start, period?.end);

  const handleDownload = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Historical Bookings");

      const sortedData = [...historicalData].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

      // Extract unique dates and meal types
      const uniqueDates = Array.from(
        new Set(sortedData.map((item) => item.date)),
      ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      const formattedDates = uniqueDates.map((d) =>
        DateTime.fromISO(d).toFormat("dd MMM yyyy"),
      );

      const mealTypes = ["Breakfast", "Lunch", "Dinner"];

      // Initialize matrix: { meal: { date: count } }
      const matrix: Record<string, Record<string, number>> = {};

      mealTypes.forEach((meal) => {
        matrix[meal] = {};
        uniqueDates.forEach((date) => {
          matrix[meal][date] = 0; // default 0
        });
      });

      // Fill matrix with real values
      for (const item of sortedData) {
        const mealLabel =
          MealDictionary[item.meal as keyof typeof MealDictionary]?.label ||
          item.meal;

        if (!matrix[mealLabel]) continue;

        matrix[mealLabel][item.date] = item.count;
      }

      // Header
      const now = DateTime.now().toFormat("dd/MM/yyyy HH:mm:ss");
      const reportHeader = `Meal Serving Report (Start: ${period?.start.toFormat("dd/MM/yyyy HH:mm:ss")} | End: ${period?.end.toFormat("dd/MM/yyyy HH:mm:ss")} | Generated at: ${now})`;
      worksheet.addRow([reportHeader]);
      worksheet.mergeCells("A1:J1");
      worksheet.getCell("A1").font = {
        bold: true,
        color: { argb: "FFFF0000" },
      };

      const headerRow = ["Date", ...formattedDates, "Total Quantity"];
      worksheet.addRow(headerRow);

      // Meal rows
      let grandTotal = 0;
      mealTypes.forEach((meal) => {
        const counts = uniqueDates.map((date) => matrix[meal][date] || 0);
        const rowTotal = counts.reduce((a, b) => a + b, 0);
        grandTotal += rowTotal;
        worksheet.addRow([meal, ...counts, rowTotal]);
      });

      // Final total row
      const totalPerDate = uniqueDates.map((date) =>
        mealTypes.reduce((sum, meal) => sum + matrix[meal][date], 0),
      );
      worksheet.addRow(["Total Quantity", ...totalPerDate, grandTotal]);

      // Column widths
      headerRow.forEach((_, idx) => {
        worksheet.getColumn(idx + 1).width = 15;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `Meal Serving Report.xlsx`);
    } catch (error) {
      console.error("Error generating Excel:", error);
      alert("Failed to generate Excel file.");
    } finally {
      onClose?.();
    }
  };

  const footer = (direction: "rtl" | "ltr") => (
    <Form.Footer direction={direction}>
      <Button
        isLoading={isLoading || isFetching}
        onClick={handleDownload}
        isDisabled={!historicalData || historicalData.length === 0}
      >
        Download
      </Button>
    </Form.Footer>
  );

  return (
    <Wrapper title={`Meal Serving Report`} footer={footer}>
      <Form data={undefined}>
        <Box px={3}>
          <Text fontSize="xs" color="gray.500">
            Select the date range for historical reports
          </Text>
        </Box>
        <HStack justifyContent={"start"} px={3} py={2}>
          <PeriodSelector period={period} shifts={[]} onChange={setPeriod} />
        </HStack>
        {!historicalData || historicalData.length === 0 ? (
          <Box px={3}>
            <Text fontSize="xs" color="gray.500" ml={2}>
              No historical data available to download for selected period.
            </Text>
          </Box>
        ) : null}
      </Form>
    </Wrapper>
  );
};

const HistoricalForm = withForm<any, any>(Historical);
export default HistoricalForm;
